import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaPython,
  FaDatabase,
  FaChartBar,
  FaGraduationCap,
  FaLaptopCode,
  FaLightbulb,
  FaBriefcase,
  FaRocket,
  FaBook,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { Link } from "react-router-dom";

const Machinelearning = () => {
  useEffect(() => {
    AOS.init({ duration: 1200, once: true });
  }, []);

  const syllabus = [
    {
      title: "Module 1: ML Fundamentals",
      topics: [
        "Introduction to Machine Learning",
        "Types of Learning",
        "Linear Algebra for ML",
        "Probability & Statistics",
        "Python for ML",
      ],
    },
    {
      title: "Module 2: Supervised Learning",
      topics: [
        "Linear & Logistic Regression",
        "Decision Trees",
        "Random Forest",
        "Support Vector Machines",
        "Naive Bayes",
      ],
    },
    {
      title: "Module 3: Unsupervised Learning",
      topics: [
        "K-Means Clustering",
        "Hierarchical Clustering",
        "Principal Component Analysis",
        "Association Rules",
        "Anomaly Detection",
      ],
    },
    {
      title: "Module 4: Advanced Topics",
      topics: [
        "Ensemble Methods",
        "Neural Networks Basics",
        "Model Selection & Tuning",
        "Cross-Validation",
        "MLOps Fundamentals",
      ],
    },
  ];

  const tools = [
    { icon: <FaPython color="#3776AB" />, name: "Python", bg: "#E3F2FD" },
    { icon: <FaDatabase color="#f29111" />, name: "SQL", bg: "#FFF3E0" },
    { icon: <FaLaptopCode color="#f2a93b" />, name: "Jupyter", bg: "#FFF8E1" },
    { icon: <FaChartBar color="#36cfc9" />, name: "Pandas", bg: "#E0F7FA" },
    { icon: <FaChartBar color="#f06595" />, name: "NumPy", bg: "#FCE4EC" },
    { icon: <FaChartBar color="#845ef7" />, name: "Matplotlib", bg: "#EDE7F6" },
    { icon: <FaChartBar color="#22b8cf" />, name: "Seaborn", bg: "#E0F2F1" },
    {
      icon: <FaLightbulb color="#ff922b" />,
      name: "Scikit-learn",
      bg: "#FFF3E0",
    },
    { icon: <FaChartBar color="#ff6b6b" />, name: "TensorFlow", bg: "#FFEBEE" },
    { icon: <FaChartBar color="#20c997" />, name: "Keras", bg: "#E8F5E9" },
  ];

  const learners = [
    {
      icon: <FaGraduationCap color="#6a11cb" />,
      name: "Data science enthusiasts",
      bg: "#F3E5F5",
    },
    {
      icon: <FaBriefcase color="#2575fc" />,
      name: "Software developers",
      bg: "#E3F2FD",
    },
    {
      icon: <FaGraduationCap color="#ff6b6b" />,
      name: "Analytics professionals",
      bg: "#FFEBEE",
    },
    {
      icon: <FaRocket color="#ff922b" />,
      name: "Research students",
      bg: "#FFF3E0",
    },
    {
      icon: <FaLightbulb color="#20c997" />,
      name: "AI/ML career aspirants",
      bg: "#E8F5E9",
    },
  ];

  const careers = [
    {
      icon: <FaBriefcase color="#6a11cb" />,
      title: "Machine Learning Engineer",
      desc: "Build ML models and deploy them.",
      bg: "#EDE7F6",
    },
    {
      icon: <FaBriefcase color="#2575fc" />,
      title: "Data Scientist",
      desc: "Analyze data and create predictive insights.",
      bg: "#E3F2FD",
    },
    {
      icon: <FaBriefcase color="#ff6b6b" />,
      title: "AI Engineer",
      desc: "Implement AI solutions and algorithms.",
      bg: "#FFEBEE",
    },
    {
      icon: <FaBriefcase color="#ff922b" />,
      title: "Research Scientist",
      desc: "Conduct ML/AI research for innovations.",
      bg: "#FFF3E0",
    },
    {
      icon: <FaBriefcase color="#20c997" />,
      title: "ML Consultant",
      desc: "Advise companies on ML strategies.",
      bg: "#E8F5E9",
    },
    {
      icon: <FaBriefcase color="#845ef7" />,
      title: "AI Product Manager",
      desc: "Manage AI/ML products lifecycle.",
      bg: "#EDE7F6",
    },
  ];

  const outcomes = [
    {
      icon: <FaBook color="#2575fc" />,
      title: "Implement ML algorithms",
      desc: "Learn how to implement popular ML algorithms from scratch.",
      bg: "#E3F2FD",
    },
    {
      icon: <FaBook color="#ff6b6b" />,
      title: "Build predictive models",
      desc: "Develop regression, classification, and clustering models.",
      bg: "#FFEBEE",
    },
    {
      icon: <FaBook color="#ff922b" />,
      title: "Evaluate model performance",
      desc: "Learn metrics, validation techniques, and model tuning.",
      bg: "#FFF3E0",
    },
    {
      icon: <FaBook color="#20c997" />,
      title: "Handle real-world datasets",
      desc: "Work with messy datasets and preprocessing techniques.",
      bg: "#E8F5E9",
    },
    {
      icon: <FaBook color="#845ef7" />,
      title: "Deploy ML models",
      desc: "Learn deployment using APIs and cloud platforms.",
      bg: "#EDE7F6",
    },
    {
      icon: <FaBook color="#36cfc9" />,
      title: "Understand deep learning basics",
      desc: "Get started with neural networks and deep learning concepts.",
      bg: "#E0F2F1",
    },
  ];

  const faq = [
    {
      question: "Do I need prior programming experience?",
      answer:
        "Basic Python knowledge helps but is not mandatory. The course guides you from fundamentals.",
    },
    {
      question: "Which tools will I learn?",
      answer:
        "Python, Scikit-learn, TensorFlow, Keras, Pandas, NumPy, Matplotlib, Jupyter, Git, Docker.",
    },
    {
      question: "Is this course suitable for beginners?",
      answer:
        "Yes! We cover core ML concepts and gradually progress to advanced topics.",
    },
    {
      question: "Will I get a certificate?",
      answer:
        "Yes, you will receive a completion certificate after finishing the course.",
    },
  ];

  const [openFAQ, setOpenFAQ] = useState(Array(faq.length).fill(false));
  const toggleFAQ = (index) => {
    const newOpen = [...openFAQ];
    newOpen[index] = !newOpen[index];
    setOpenFAQ(newOpen);
  };

  return (
    <div className="overflow-hidden">
      <style>{`
        body { font-family: 'Poppins', sans-serif; margin:0; padding:0; background:#f5f6fa; color:#333; }
        .container{width:90%;max-width:1200px;margin:auto;padding:2rem 0;}
        .hero-section{background-image:url('https://media.istockphoto.com/id/1202870693/photo/future-artificial-intelligence-robot-and-cyborg.jpg?s=612x612&w=0&k=20&c=Y1e8NLCRVg-_AM2JBv0NLQjJZo5gGfaO4mVsojNywFE=');background-size:cover;background-position:center;color:#fff;text-align:center;padding:6rem 1rem;position:relative;animation:fadeIn 2s;}
        .hero-section::before{content:"";position:absolute;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);}
        .hero-section .container{position:relative;z-index:1;}
        .hero-section h1{font-size:3rem;margin-bottom:1rem;animation:bounce 2s infinite;}
        .hero-section p{font-size:1.2rem;margin-bottom:2rem;}
        .course-info span{display:inline-block;margin:0 1rem;font-weight:600;background: rgba(255,255,255,0.2);padding:0.5rem 1rem;border-radius:25px;}
        .btn{display:inline-block;background:#ff6b6b;color:#fff;padding:0.8rem 2rem;border-radius:30px;text-decoration:none;margin-top:1.5rem;font-weight:600;transition:0.3s;}
        .btn:hover{background:#ff4757;transform:scale(1.05);}
        section h2{text-align:center;margin-bottom:2rem;font-size:2rem;color:#111;}
        .about-section-heading{text-align:center;margin-bottom:1rem;font-size:2rem;font-weight:bold;}
        .about-course{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:2rem;padding:4rem 0;}
        .about-course img{max-width:400px;border-radius:15px;box-shadow:0 10px 30px rgba(0,0,0,0.1);animation:float 3s ease-in-out infinite;}
        .about-course p{max-width:600px;font-size:1.1rem;line-height:1.6;text-align:center;}
        .syllabus-cards,.tools-cards,.learners-cards,.careers-cards,.learning-outcomes{display:grid;grid-template-columns:repeat(auto-fit,minmax(250px,1fr));gap:1rem;}
        .module-card,.card,.learning-outcomes li{border-radius:15px;padding:1.5rem;text-align:center;transition:transform 0.3s,box-shadow 0.3s;box-shadow:0 6px 15px rgba(0,0,0,0.1);}
        .module-card:hover,.card:hover,.learning-outcomes li:hover{transform:translateY(-10px);box-shadow:0 12px 25px rgba(0,0,0,0.2);}
        .card-icon{font-size:2rem;margin-bottom:0.5rem;display:flex;justify-content:center;align-items:center;animation: pulse 2s infinite;}
        .faq-card{background:#fff;padding:1rem;border-radius:15px;box-shadow:0 6px 15px rgba(0,0,0,0.1);margin-bottom:1rem;cursor:pointer;transition:0.3s;}
        .faq-card:hover{transform:translateY(-3px);box-shadow:0 10px 20px rgba(0,0,0,0.15);}
        .faq-answer{margin-top:0.5rem;text-align:left;padding-left:2rem;}
        footer{background:#11998e;color:#fff;padding:3rem 1rem;text-align:center;}
        footer .btn{background:#ff6b6b;padding:1rem 3rem;font-size:1.1rem;}
        @keyframes bounce{0%,20%,50%,80%,100%{transform:translateY(0);}40%{transform:translateY(-15px);}60%{transform:translateY(-8px);}}
        @keyframes float{0%,100%{transform:translateY(0);}50%{transform:translateY(-15px);}}
        @keyframes pulse{0%,100%{transform:scale(1);}50%{transform:scale(1.05);}}
        @media(max-width:768px){.learning-outcomes,.careers-cards{grid-template-columns:1fr 1fr;}}
      `}</style>

      {/* Hero Section */}
      <header className="hero-section" data-aos="fade-down">
        <div className="container">
          <h1>Machine Learning Mastery</h1>
          <p>Master ML algorithms and build intelligent applications</p>
          <Link to="/contact" className="btn">
            Enroll Now
          </Link>
        </div>
      </header>

      {/* About Section */}
      <section
        className="about-course"
        data-aos="fade-up"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "2rem",
          flexWrap: "wrap",
          maxWidth: "1200px",
          margin: "0 auto",
          padding: "2rem 1rem",
        }}
      >
        {/* Image on the left */}
        <img
          src="/assets/360_F_258871009_f5net6t178mMF1nekdg2AS2vuOUhpDjL.jpg"
          alt="Machine Learning Course"
          style={{
            width: "45%",
            minWidth: "300px",
            borderRadius: "15px",
            boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
          }}
        />

        {/* Content on the right */}
        <div style={{ width: "50%", minWidth: "300px", textAlign: "justify" }}>
          <h2
            style={{
              fontSize: "1.8rem",
              fontWeight: "700",
              marginBottom: "1.2rem",
              color: "#000",
            }}
            data-aos="fade-up"
          >
            About This Machine Learning Course
          </h2>
          <p
            style={{
              fontSize: "0.95rem",
              lineHeight: "1.7",
              textAlign: "justify",
            }}
          >
            This Machine Learning course provides a solid foundation in modern
            ML techniques and real-world applications. You will learn data
            preprocessing, feature engineering, and core algorithms for
            supervised and unsupervised learning. The course emphasizes hands-on
            practice using Python, Scikit-learn, TensorFlow, and Keras to build
            predictive models and neural networks. Advanced topics such as
            ensemble methods, natural language processing, and model evaluation
            are also covered through practical projects and case studies. By the
            end, you will confidently apply ML techniques to solve real-world
            problems and advance your career in AI and data analytics.
          </p>
        </div>
      </section>

      {/* Syllabus Section */}
      <section className="py-16 max-w-6xl mx-auto px-6" data-aos="fade-up">
        <h2 className="text-3xl font-bold text-center mb-12">
          Course Syllabus
        </h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {syllabus.map((mod, idx) => {
            const syllabusColors = ["#ff9800", "#4caf50", "#2196f3", "#e91e63"];
            const color = syllabusColors[idx % syllabusColors.length];

            return (
              <div
                key={idx}
                className="relative p-6 bg-white border rounded-xl shadow-md hover:shadow-lg transition transform hover:scale-105 animate-shake"
                data-aos="fade-up"
                data-aos-delay={idx * 100}
              >
                {/* Gradient corner top-left */}
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "40px",
                    height: "40px",
                    borderTopLeftRadius: "12px",
                    background: `linear-gradient(135deg, ${color}, transparent)`,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                ></div>

                {/* Gradient corner bottom-right */}
                <div
                  style={{
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: "40px",
                    height: "40px",
                    borderBottomRightRadius: "12px",
                    background: `linear-gradient(315deg, ${color}, transparent)`,
                    boxShadow: `0 0 8px ${color}`,
                  }}
                ></div>

                {/* Icon */}
                <div
                  className="icon-circle flex justify-center items-center mb-4"
                  style={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "50%",
                    border: `2px solid ${color}`,
                    margin: "0 auto",
                    fontSize: "1.8rem",
                    color: color,
                  }}
                >
                  <FaBook />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-center mb-3">
                  {mod.title}
                </h3>

                {/* Description */}
                {mod.desc && (
                  <p className="text-gray-700 text-center mb-3">{mod.desc}</p>
                )}

                {/* Topics */}
                <ul className="text-gray-600 list-disc list-inside text-left">
                  {mod.topics.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      </section>

      {/* Tools Section */}
      <section className="tools-technologies" data-aos="fade-up">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            Tools & Technologies You'll Master
          </h2>
          <div className="tools-cards animate-pulse">
            {tools.map((tool, idx) => (
              <div
                key={idx}
                className="card"
                data-aos="flip-left"
                data-aos-delay={idx * 50}
                style={{ background: tool.bg }}
              >
                <div className="card-icon">{tool.icon}</div>
                <h3>{tool.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learners */}
      <section className="who-can-enroll" data-aos="fade-up">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            Who Can Enroll?
          </h2>
          <div className="learners-cards animate-bounce-slow">
            {learners.map((learner, idx) => (
              <div
                key={idx}
                className="card"
                data-aos="fade-right"
                data-aos-delay={idx * 50}
                style={{ background: learner.bg }}
              >
                <div className="card-icon">{learner.icon}</div>
                <h3>{learner.name}</h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Careers */}
      <section className="career-opportunities" data-aos="fade-up">
        <div className="container">
          <h2 className="text-3xl font-bold text-center mb-12">
            Career Opportunities
          </h2>
          <div className="careers-cards">
            {careers.map((career, idx) => (
              <div
                key={idx}
                className="card animate-wiggle"
                data-aos="fade-left"
                data-aos-delay={idx * 50}
                style={{ background: career.bg }}
              >
                <div className="card-icon">{career.icon}</div>
                <h3>{career.title}</h3>
                <p>{career.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className="faq-section"
        data-aos="fade-up"
        style={{
          padding: "4rem 0",
          background: "linear-gradient(135deg, #f0f4f8, #d9e2ec)",
        }}
      >
        <div className="container">
          <h2
            style={{
              textAlign: "center",
              fontSize: "2rem",
              fontWeight: "700",
              marginBottom: "2rem",
            }}
          >
            Frequently Asked Questions
          </h2>
          {faq.map((q, idx) => {
            const cardColors = [
              "linear-gradient(135deg, #ffecd2, #fcb69f)",
              "linear-gradient(135deg, #cfd9df, #e2ebf0)",
              "linear-gradient(135deg, #f9d5e5, #fbc1cc)",
            ];
            const cardColor = cardColors[idx % cardColors.length];

            return (
              <div
                key={idx}
                className="faq-card"
                onClick={() => toggleFAQ(idx)}
                style={{
                  background: cardColor,
                  padding: "1rem 1.5rem",
                  borderRadius: "15px",
                  marginBottom: "1rem",
                  boxShadow: "0 5px 15px rgba(0,0,0,0.1)",
                  cursor: "pointer",
                  transition: "0.3s",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <h3 style={{ margin: 0 }}>{q.question}</h3>
                  {openFAQ[idx] ? <FaChevronUp /> : <FaChevronDown />}
                </div>
                {openFAQ[idx] && (
                  <p
                    className="faq-answer"
                    style={{
                      marginTop: "0.5rem",
                      fontSize: "0.95rem",
                      lineHeight: "1.6",
                      color: "#333",
                    }}
                  >
                    {q.answer}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer
        style={{ background: "#111", color: "#fff", padding: "4rem 1rem" }}
      >
        <div
          className="container"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "1.5rem",
            textAlign: "center",
          }}
        >
          <h2 style={{ fontSize: "2rem", fontWeight: "700", color: "#6c5ce7" }}>
            Ready to Start Your Machine Learning Journey?
          </h2>
          <p style={{ maxWidth: "600px", lineHeight: "1.6", color: "#ccc" }}>
            Enroll today and gain practical experience with ML tools,
            algorithms, and real-world datasets. Build predictive models, work
            on hands-on projects, and boost your career in AI and data
            analytics.
          </p>
          <Link
            to="/contact"
            className="btn"
            style={{
              background: "#6c5ce7",
              color: "#fff",
              padding: "0.8rem 2rem",
              borderRadius: "30px",
              fontWeight: "600",
              textDecoration: "none",
              transition: "0.3s",
            }}
            onMouseEnter={(e) => (e.target.style.transform = "scale(1.05)")}
            onMouseLeave={(e) => (e.target.style.transform = "scale(1)")}
          >
            Enroll Now
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default Machinelearning;
